import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import * as api from 'config/api';
import {
  DebtAssetAccountHistories,
  DebtAssetAccountLists,
  GetDebtAssetResult,
  updateUser,
} from 'config/api';
import { useHomeUser, isLoading } from 'hooks/useHomeUser';
import Loading from 'components/commons/loading/Loading';
import { getDate } from 'components/features/utils/converter';
import { DataLoadingProps } from 'components/features/home/assetStuffing/AssetStuffing';
import { PortfolioStuffingChart } from './PortfolioStuffingChart';
import { AssetStuffingDebtList } from './AssetStuffingDebtList';

const noDataColor = '#6366F1';
const colors = [
  '#6366F1',
  '#38BDF8',
  '#F472B6',
  '#22C55E',
  '#F97316',
  '#FACC15',
  '#C026D3',
  '#0891B2',
  '#A16207',
  '#FCA5A5',
];

type AssetStuffingProps = {
  UserBrokerUpdateTime: React.ReactElement;
  groupKey: number;
};

type DebtAsset = {
  debtAsset: GetDebtAssetResult;
};

export type AccountHistories = {
  AccountName: string;
  AccountColor: string;
  Histories: {
    Day: Date;
    DebtAmount: number;
  }[];
};

const NoDebit = () => {
  return (
    <div className="flex justify-center py-12 pl-3 bg-bluegray-600">
      <img className="" src="/static/images/no_debt.svg" alt="" />
    </div>
  );
};

const DebitLoading = () => {
  return (
    <>
      <div className="bg-bluegray-600">
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '140px',
            paddingBottom: '140px',
          }}
        />
      </div>
      <div className="p-4">
        <div className="flex flex-row mt-8 mb-4">
          <div className="text-sm font-bold">負債総額</div>
        </div>
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        />
      </div>
    </>
  );
};

const AssetStuffingDebtInternal = ({
  UserBrokerUpdateTime,
  groupKey,
  debtAsset,
  isFetching,
  isRefetching,
}: AssetStuffingProps & DebtAsset & DataLoadingProps) => {
  const { homeUser, getHomeUser } = useHomeUser(200);
  const [isHiddenAssetD, setIsHiddenAssetD] = useState<boolean | null>(null);

  const accountLists = debtAsset.TotalAsset.AccountLists;
  const accountHistories = debtAsset.TotalAsset.AccountHistories;
  const groupAssets = debtAsset.GroupAssets;

  const sortedAccountLists = accountLists.sort((a, b) => {
    return a.RealDebtAmount > b.RealDebtAmount ? 1 : -1;
  });
  const colorList = sortedAccountLists.map((list, i) => {
    const num = Number(i.toString().slice(-1));
    const color = colors[num] !== undefined ? colors[num] : noDataColor;
    return {
      account: list.Account,
      color: color,
    };
  });

  const getAccountColor = (acount: string) => {
    const list = colorList.find(list => list.account === acount);
    return list !== undefined ? list.color : noDataColor;
  };

  const getGroupAssetsAccountLists = (key: number) => {
    const list = groupAssets.find(a => Number(a.ID) === key)
      ?.AccountLists as DebtAssetAccountLists[];
    return list !== undefined ? list : null;
  };

  const dispAccountLists = accountLists.filter(
    list => list.RealDebtAmount !== 0
  );

  const debtAssetsList: DebtAssetAccountLists[] | null =
    groupKey === 0 ? dispAccountLists : getGroupAssetsAccountLists(groupKey);

  useEffect(() => {
    getHomeUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (homeUser.tag === 'LOADED') {
      const value = homeUser.isHiddenAssetD.value;
      setIsHiddenAssetD(value);
    }
  }, [homeUser]);

  if (isLoading(homeUser)) return <DebitLoading />;

  if (debtAssetsList === null || debtAssetsList.length === 0)
    return <NoDebit />;

  const sortedDebtAssetsList = debtAssetsList.sort((a, b) => {
    return a.RealDebtAmount > b.RealDebtAmount ? 1 : -1;
  });

  const getGroupAssetsAccountHistories = (key: number) => {
    const list = groupAssets.find(list => Number(list.ID) === key)
      ?.AccountHistories;
    return list !== undefined ? list : null;
  };

  const debtAssetsHistories: DebtAssetAccountHistories[] | null =
    groupKey === 0
      ? accountHistories
      : getGroupAssetsAccountHistories(groupKey);

  if (debtAssetsHistories === null) return <></>;

  const dispDebtAssetsHistories = debtAssetsHistories
    .map(list => {
      const item = list.Histories.map(histories => {
        return histories.DebtAmount !== 0 ? histories : null;
      }).filter((item): item is NonNullable<typeof item> => item != null);

      return {
        Account: list.Account,
        AccountName: list.AccountName,
        Histories: item,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item != null);

  const chartDebtAssetsHistories: AccountHistories[] = dispDebtAssetsHistories.map(
    list => {
      return {
        AccountName: list.AccountName,
        AccountColor: getAccountColor(list.Account),
        Histories: list.Histories.map(item => {
          return {
            Day: getDate(item.Day),
            DebtAmount: item.DebtAmount,
          };
        }),
      };
    }
  );

  const updateHideDebt = async () => {
    setIsHiddenAssetD(!isHiddenAssetD);
    await updateUser({
      HideAssetD: !isHiddenAssetD,
    });
  };

  return (
    <>
      {accountLists.length === 0 ||
      accountHistories.length === 0 ||
      groupAssets.length === 0 ? (
        <NoDebit />
      ) : (
        <>
          <div className="pt-4 bg-bluegray-600">
            {isFetching && isRefetching ? (
              <>
                <Loading
                  loading={true}
                  circularLoading={true}
                  style={{
                    paddingTop: '130px',
                    paddingBottom: '130px',
                  }}
                />
              </>
            ) : (
              <PortfolioStuffingChart
                {...{ accountHistories: chartDebtAssetsHistories }}
              />
            )}
          </div>
          <div className="flex flex-row items-center relative z-10 -mt-6 mb-4 bg-bluegray-600">
            {UserBrokerUpdateTime}
          </div>
          <AssetStuffingDebtList
            {...{
              sortedDebtAssetsList,
              isHiddenAssetD,
              getAccountColor,
              updateHideDebt,
            }}
          />
        </>
      )}
    </>
  );
};

export const PortfolioStuffingDebt = ({
  UserBrokerUpdateTime,
  groupKey,
}: AssetStuffingProps): React.ReactElement => {
  const { data: debtAssetData, isFetching, isRefetching, refetch } = useQuery(
    'portfolioStuffingDebtAsset',
    fetchDebtAsset,
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    (async () => {
      await refetch();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey]);

  if (debtAssetData === null || debtAssetData === undefined)
    return <DebitLoading />;

  return (
    <AssetStuffingDebtInternal
      {...{
        UserBrokerUpdateTime,
        groupKey,
        debtAsset: debtAssetData as GetDebtAssetResult,
        isFetching,
        isRefetching,
      }}
    />
  );
};

const fetchDebtAsset = async () => {
  return await api.getDebtAsset();
};
